/**
 * AuthorizationManagement.jsx - Operational authorization management page
 * 
 * Sprint D — Authorization Light
 * Uses PageShell layout and apiClient (no raw axios)
 * 
 * Features:
 * - List authorizations with status badges
 * - Filter by type, status, SAIL level
 * - CRUD operations
 * - RBAC: pilots see read-only, compliance_manager can revoke but not delete
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { 
  Award, Plus, Edit2, Trash2, FileText, Calendar, Filter, 
  CheckCircle, Clock, XCircle, Ban, AlertTriangle, ArrowRight,
  ChevronDown, ChevronUp, RefreshCw, History,
  FileEdit, Send, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import cs from '../locales/cs';
import en from '../locales/en';
import { useAuth } from '../contexts/AuthContext';
// Wave 3 Block 3A components
import RevisionTimeline from '../components/authorization/RevisionTimeline';
import CommentForm from '../components/authorization/CommentForm';
import StateTransitionModal from '../components/authorization/StateTransitionModal';
// Sprint Y Block E - Correspondence Tab
import { CorrespondenceTab } from '../components/correspondence';

const SAIL_LEVELS = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const AUTH_TYPES = ['STS_01', 'STS_02', 'PDRA_S01', 'PDRA_S02', 'PDRA_G01', 'LUC', 'OPERATIONAL_AUTHORIZATION', 'CUSTOM'];
const AUTH_STATUSES = ['active', 'expired', 'suspended', 'revoked'];

export default function AuthorizationManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'cs');
  const t = language === 'cs' ? cs.authorizations : en.authorizations;
  const tCommon = language === 'cs' ? cs.common : en.common;
  
  const [authorizations, setAuthorizations] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sailFilter, setSailFilter] = useState('all');
  
  // Delete/Revoke confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  // Wave 3 Block 3A: Expanded row and transition modal state
  const [expandedAuthId, setExpandedAuthId] = useState(null);
  const [transitionModalAuth, setTransitionModalAuth] = useState(null);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);

  // RBAC
  const isAdmin = user?.role === 'platform_admin' || user?.role === 'organization_admin';
  const canWrite = isAdmin || user?.role === 'compliance_manager';
  const canDelete = isAdmin;

  // Fetch authorizations
  const fetchAuthorizations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('type_filter', typeFilter);
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (sailFilter !== 'all') params.append('sail_level', sailFilter);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [authRes, statusRes] = await Promise.all([
        api.get(`/authorizations${queryString}`),
        api.get('/authorizations/status')
      ]);
      
      setAuthorizations(authRes.data);
      setStatusData(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch authorizations:', err);
      setError(err.response?.data?.detail || 'Failed to load authorizations');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, sailFilter]);

  useEffect(() => {
    fetchAuthorizations();
  }, [fetchAuthorizations]);

  // Listen for language changes
  useEffect(() => {
    const handleStorage = () => {
      setLanguage(localStorage.getItem('language') || 'cs');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Delete authorization (hard delete - admin only)
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/authorizations/${deleteConfirm.id}`);
      toast.success(t.authorizationDeleted);
      setDeleteConfirm(null);
      fetchAuthorizations();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete authorization');
    }
  };

  // Revoke authorization (soft delete - compliance_manager can do this)
  const handleRevoke = async () => {
    if (!revokeConfirm) return;
    
    try {
      await api.patch(`/authorizations/${revokeConfirm.id}/status`, { 
        status: 'revoked',
        reason: 'Revoked by user'
      });
      toast.success(t.authorizationRevoked);
      setRevokeConfirm(null);
      fetchAuthorizations();
    } catch (err) {
      console.error('Revoke failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to revoke authorization');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US');
  };

  // Get status badge
  const getStatusBadge = (auth) => {
    const status = auth.status;
    const daysUntil = auth.days_until_expiry;
    
    if (status === 'active' && daysUntil >= 0 && daysUntil <= 30) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1" data-testid="status-expiring">
          <Clock className="h-3 w-3" />
          {t.expiringAuthorizations}
        </Badge>
      );
    }
    
    switch (status) {
      // Application stages (new)
      case 'draft':
        return (
          <Badge className="bg-slate-100 text-slate-600 border-slate-200 gap-1" data-testid="status-draft">
            <FileEdit className="h-3 w-3" />
            {t.status_draft || t.statuses?.draft || 'Draft'}
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-1" data-testid="status-submitted">
            <Send className="h-3 w-3" />
            {t.status_submitted || t.statuses?.submitted || 'Submitted'}
          </Badge>
        );
      case 'under_review':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1" data-testid="status-under-review">
            <Clock className="h-3 w-3" />
            {t.status_under_review || t.statuses?.under_review || 'Under Review'}
          </Badge>
        );
      case 'needs_amendment':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 gap-1" data-testid="status-needs-amendment">
            <AlertCircle className="h-3 w-3" />
            {t.status_needs_amendment || t.statuses?.needs_amendment || 'Needs Amendment'}
          </Badge>
        );
      // Issued stages (existing)
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200 gap-1" data-testid="status-active">
            <CheckCircle className="h-3 w-3" />
            {t.status_active || t.statuses?.active || 'Active'}
          </Badge>
        );
      case 'expired':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200 gap-1" data-testid="status-expired">
            <XCircle className="h-3 w-3" />
            {t.status_expired || t.statuses?.expired || 'Expired'}
          </Badge>
        );
      case 'suspended':
        return (
          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 gap-1" data-testid="status-suspended">
            <AlertTriangle className="h-3 w-3" />
            {t.status_suspended || t.statuses?.suspended || 'Suspended'}
          </Badge>
        );
      case 'revoked':
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-200 gap-1" data-testid="status-revoked">
            <Ban className="h-3 w-3" />
            {t.status_revoked || t.statuses?.revoked || 'Revoked'}
          </Badge>
        );
      default:
        return null;
    }
  };

  // Get type label
  const getTypeLabel = (type) => {
    return t.types?.[type] || type;
  };

  // Page actions
  const pageActions = canWrite ? (
    <Button 
      onClick={() => navigate('/authorizations/new')} 
      className="gap-2 bg-[var(--brand-600)] hover:bg-[var(--brand-700)]"
      data-testid="add-authorization-btn"
    >
      <Plus className="h-4 w-4" />
      {t.addAuthorization}
    </Button>
  ) : null;

  return (
    <PageShell
      title={t.title}
      icon={Award}
      actions={pageActions}
      loading={loading}
      error={error}
    >
      <PageShell.Content className="space-y-6">
        {/* Stats Cards */}
        {statusData && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="stats-cards">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">{t.totalAuthorizations}</p>
                    <p className="text-2xl font-bold text-[var(--text-primary)]">{statusData.total_authorizations}</p>
                  </div>
                  <Award className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">{t.activeAuthorizations}</p>
                    <p className="text-2xl font-bold text-green-600">{statusData.active_authorizations}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">{t.expiringAuthorizations}</p>
                    <p className="text-2xl font-bold text-amber-600">{statusData.expiring_authorizations}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">{t.expiredAuthorizations}</p>
                    <p className="text-2xl font-bold text-red-600">{statusData.expired_authorizations}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">{t.suspendedAuthorizations}</p>
                    <p className="text-2xl font-bold text-orange-600">{statusData.suspended_authorizations}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div>
                  <p className="text-xs text-[var(--text-muted)]">{t.coveredSailLevels}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {statusData.covered_sail_levels?.length > 0 ? (
                      statusData.covered_sail_levels.map(level => (
                        <Badge key={level} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          SAIL {level}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-[var(--text-muted)]">-</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4" data-testid="filters">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[var(--text-muted)]" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48" data-testid="type-filter">
                <SelectValue placeholder={t.filterByType} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allTypes}</SelectItem>
                {AUTH_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{getTypeLabel(type)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder={t.filterByStatus} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allStatuses}</SelectItem>
              {AUTH_STATUSES.map(status => (
                <SelectItem key={status} value={status}>{t[`status_${status}`]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sailFilter} onValueChange={setSailFilter}>
            <SelectTrigger className="w-40" data-testid="sail-filter">
              <SelectValue placeholder={t.filterBySail} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allSailLevels}</SelectItem>
              {SAIL_LEVELS.map(level => (
                <SelectItem key={level} value={level}>SAIL {level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Authorizations Table */}
        {authorizations.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="py-12 text-center">
              <Award className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-[var(--text-primary)]">{t.noAuthorizations}</h3>
              <p className="text-sm text-[var(--text-muted)] mt-1">{t.noAuthorizationsDesc}</p>
              {canWrite && (
                <Button 
                  onClick={() => navigate('/authorizations/new')} 
                  className="mt-4 gap-2" 
                  variant="outline"
                >
                  <Plus className="h-4 w-4" />
                  {t.addAuthorization}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="authorizations-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800 border-b">
                    <tr>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-2 py-3 w-8"></th>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.referenceNumber}</th>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.authorizationType}</th>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.authTitle}</th>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.sailLevels}</th>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.validUntil}</th>
                      <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.status}</th>
                      <th className="text-right text-xs font-medium text-[var(--text-muted)] px-4 py-3">{tCommon.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {authorizations.map((auth) => (
                      <Collapsible 
                        key={auth.id} 
                        asChild
                        open={expandedAuthId === auth.id}
                        onOpenChange={(open) => setExpandedAuthId(open ? auth.id : null)}
                      >
                        <>
                          <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`auth-row-${auth.id}`}>
                            {/* Expand toggle */}
                            <td className="px-2 py-3 w-8">
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  {expandedAuthId === auth.id ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-medium text-[var(--text-primary)]">{auth.reference_number}</span>
                            </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">
                            {getTypeLabel(auth.type)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-[var(--text-secondary)]">{auth.title}</span>
                          {auth.conditions?.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {auth.conditions.slice(0, 2).map((cond, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-slate-50">
                                  {cond.length > 25 ? cond.substring(0, 25) + '...' : cond}
                                </Badge>
                              ))}
                              {auth.conditions.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{auth.conditions.length - 2}</Badge>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {auth.applicable_sail_levels?.map(level => (
                              <Badge key={level} className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                                {level}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-[var(--text-muted)]" />
                            <span className="text-sm text-[var(--text-secondary)]">{formatDate(auth.valid_until)}</span>
                          </div>
                          <span className="text-xs text-[var(--text-muted)]">
                            {auth.days_until_expiry >= 0 
                              ? `${auth.days_until_expiry} ${t.daysRemaining}`
                              : `${Math.abs(auth.days_until_expiry)} ${t.daysAgo}`
                            }
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(auth)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Wave 3 Block 3A: State transition button */}
                            {canWrite && auth.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                                onClick={() => setTransitionModalAuth(auth)}
                                title={language === 'cs' ? 'Změnit stav' : 'Change State'}
                                data-testid={`transition-auth-${auth.id}`}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            {auth.document_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${auth.document_url}`, '_blank')}
                                title={t.viewPdf}
                                data-testid={`view-pdf-${auth.id}`}
                              >
                                <FileText className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => navigate(`/authorizations/${auth.id}/edit`)}
                                data-testid={`edit-auth-${auth.id}`}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            )}
                            {canWrite && auth.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                                onClick={() => setRevokeConfirm(auth)}
                                title={t.revokeAuthorization}
                                data-testid={`revoke-auth-${auth.id}`}
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                onClick={() => setDeleteConfirm(auth)}
                                data-testid={`delete-auth-${auth.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Wave 3 Block 3A: Expandable timeline row */}
                      <CollapsibleContent asChild>
                        <tr>
                          <td colSpan="8" className="p-0">
                            <div className="bg-slate-50 dark:bg-slate-900/50 border-t px-6 py-4">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* Timeline */}
                                <div>
                                  <RevisionTimeline
                                    key={`timeline-${auth.id}-${timelineRefreshKey}`}
                                    authorizationId={auth.id}
                                    language={language}
                                    onRefresh={() => setTimelineRefreshKey(k => k + 1)}
                                  />
                                </div>
                                {/* Comment form */}
                                <div>
                                  <CommentForm
                                    authorizationId={auth.id}
                                    language={language}
                                    onCommentAdded={() => setTimelineRefreshKey(k => k + 1)}
                                    disabled={!canWrite}
                                  />
                                </div>
                                {/* Sprint Y Block E: Correspondence Tab */}
                                <div>
                                  <CorrespondenceTab
                                    entityType="authorization"
                                    entityId={auth.id}
                                    includeProposed={true}
                                    compact={true}
                                    maxItems={5}
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </CollapsibleContent>
                      </>
                      </Collapsible>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.deleteAuthorization}</AlertDialogTitle>
              <AlertDialogDescription>
                {t.deleteConfirm}
                <br />
                <span className="text-red-600 font-medium">{t.deleteWarning}</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tCommon.cancel}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                {t.deleteAuthorization}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Revoke Confirmation */}
        <AlertDialog open={!!revokeConfirm} onOpenChange={() => setRevokeConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.revokeAuthorization}</AlertDialogTitle>
              <AlertDialogDescription>
                {t.revokeConfirm}
                <br />
                <span className="text-orange-600 font-medium">{t.revokeWarning}</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tCommon.cancel}</AlertDialogCancel>
              <AlertDialogAction onClick={handleRevoke} className="bg-orange-600 hover:bg-orange-700">
                {t.revokeAuthorization}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Wave 3 Block 3A: State Transition Modal */}
        <StateTransitionModal
          open={!!transitionModalAuth}
          onClose={() => setTransitionModalAuth(null)}
          authorizationId={transitionModalAuth?.id}
          language={language}
          onTransitionComplete={() => {
            setTimelineRefreshKey(k => k + 1);
            fetchAuthorizations();
          }}
        />
      </PageShell.Content>
    </PageShell>
  );
}
